/**
 * The signs on the buses: over the windscreen and on the right by the front
 * door, the line's number in its own colours and where it is going in amber
 * LEDs, as on Västtrafik's. The buses themselves are instanced, one mesh a
 * design (`traffic.ts`), and a sign says something different on each, so
 * the signs are meshes of their own, a pair per bus, placed each frame.
 * A texture is drawn once per line and destination and shared.
 */

import { CanvasTexture, DoubleSide, Euler, Group, Mesh, MeshBasicMaterial, PlaneGeometry, Quaternion, SRGBColorSpace, Vector3 } from 'three';
import type { VehicleSpecies } from '../core/build/parking.js';
import type { TransitTrip } from '../core/transit/trip.js';
import { BUSES } from './Bus.js';
import type { BusKind } from './Vehicle.js';

/**
 * A vehicle with signs, where it is, which way, its trip: a bus, its signs
 * where its design has them (`species`), or anything else with its own
 * places for them (`front`, `side`), as a tram's front section.
 */
export interface SignPose {
  id: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  grade: number;
  trip: TransitTrip;
  species?: VehicleSpecies;
  /** Where the front sign goes along it (z, forward negative) and how high, metres. */
  front?: number;
  frontY?: number;
  /** Where the side sign goes on its right. */
  side?: { x: number; y: number; z: number };
}

const FRONT = new PlaneGeometry(2.15, 0.27);
const SIDE = new PlaneGeometry(1.25, 0.22);
/** Over the windscreen, and on the right side by the front door, metres in the bus's frame. */
const FRONT_Y = 2.8;
const SIDE_Y = 2.42;
/** A plane faces +z: the front sign turned to face forward (-z), the side one to the right (+x). */
const AHEAD = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI);
const RIGHT = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI / 2);
const euler = new Euler();
const turned = new Quaternion();
const origin = new Vector3();

export class BusSigns {
  readonly group = new Group();
  private readonly signs = new Map<number, { front: Mesh; side: Mesh; key: string }>();
  private readonly textures = new Map<string, { material: MeshBasicMaterial; users: number }>();

  /** Each frame: a pair of signs on every bus out, and none on one gone. */
  update(buses: readonly SignPose[]): void {
    const seen = new Set<number>();
    for (const bus of buses) {
      const spec = bus.species === undefined ? undefined : BUSES[bus.species as BusKind] as { front: number } | undefined;
      const front = bus.front ?? (spec === undefined ? undefined : spec.front - 0.06);
      if (front === undefined) continue;
      seen.add(bus.id);
      const key = `${bus.trip.line.name}|${bus.trip.headsign}|${bus.trip.line.background}|${bus.trip.line.foreground}`;
      let sign = this.signs.get(bus.id);
      if (sign !== undefined && sign.key !== key) {
        this.release(sign.key);
        sign.front.removeFromParent();
        sign.side.removeFromParent();
        sign = undefined;
      }
      if (sign === undefined) {
        const material = this.material(key, bus.trip);
        sign = { front: new Mesh(FRONT, material), side: new Mesh(SIDE, material), key };
        this.group.add(sign.front, sign.side);
        this.signs.set(bus.id, sign);
      }
      // The bus's frame as the traffic draws it: turned by its yaw, tipped by its grade.
      euler.set(Math.atan(bus.grade), bus.yaw, 0, 'YXZ');
      turned.setFromEuler(euler);
      const place = (m: Mesh, x: number, y: number, z: number, facing: Quaternion) => {
        m.position.set(x, y, z).applyQuaternion(turned).add(origin.set(bus.x, bus.y, bus.z));
        m.quaternion.copy(turned).multiply(facing);
      };
      place(sign.front, 0, bus.frontY ?? FRONT_Y, front, AHEAD);
      const side = bus.side ?? { x: 1.31, y: SIDE_Y, z: front + 1.96 };
      place(sign.side, side.x, side.y, side.z, RIGHT);
    }
    for (const [id, sign] of this.signs) {
      if (seen.has(id)) continue;
      this.release(sign.key);
      sign.front.removeFromParent();
      sign.side.removeFromParent();
      this.signs.delete(id);
    }
  }

  private material(key: string, trip: TransitTrip): MeshBasicMaterial {
    const had = this.textures.get(key);
    if (had !== undefined) { had.users++; return had.material; }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 64;
    const g = canvas.getContext('2d')!;
    g.fillStyle = '#0b0c0d';
    g.fillRect(0, 0, 512, 64);
    // The line's number in its own colours, boxed at the left.
    const name = trip.line.name.length > 6 ? trip.line.name.split(/\s+/)[0] : trip.line.name;
    g.font = 'bold 40px sans-serif';
    const w = Math.max(64, g.measureText(name).width + 24);
    g.fillStyle = trip.line.background;
    g.fillRect(4, 4, w, 56);
    g.fillStyle = trip.line.foreground;
    g.textBaseline = 'middle';
    g.fillText(name, 4 + (w - g.measureText(name).width) / 2, 34);
    // Where it is going, in amber, fitted to the rest.
    g.fillStyle = '#ffb21e';
    let size = 38;
    g.font = `bold ${size}px sans-serif`;
    while (size > 18 && g.measureText(trip.headsign).width > 500 - w - 24) g.font = `bold ${--size}px sans-serif`;
    g.fillText(trip.headsign, w + 20, 34);
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.anisotropy = 4;
    const material = new MeshBasicMaterial({ map: texture, side: DoubleSide, toneMapped: false });
    this.textures.set(key, { material, users: 1 });
    return material;
  }

  private release(key: string): void {
    const t = this.textures.get(key);
    if (t === undefined) return;
    if (--t.users > 0) return;
    t.material.map?.dispose();
    t.material.dispose();
    this.textures.delete(key);
  }
}
